import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { FavouritesProvider } from "./context/FavouritesContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useConfig } from "./hooks/useConfig";
import { SubscriptionsProvider } from "./context/SubscriptionsContext";
import { LocationProvider } from "./context/LocationContext";
import { FxProvider } from "./context/FxContext";
import { LanguageProvider } from "./context/LanguageContext";
import { LangSync } from "./components/LangSync";
import { LangGeoHint } from "./components/LangGeoHint";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import BrowseDeals from "./pages/BrowseDeals";
import Categories from "./pages/Categories";
import NearExpiryPage from "./pages/NearExpiryPage";
import Favourites from "./pages/Favourites";
import MyDeals from "./pages/MyDeals";
import Businesses from "./pages/Businesses";
import Account from "./pages/Account";
import DealDetail from "./pages/DealDetail";
import PostDeal from "./pages/PostDeal";
import Analytics from "./pages/Analytics";
// /analytics-features (the plan comparison table) is parked for now — the route redirects home.
import Login from "./pages/Login";
import Register from "./pages/Register";
import Signup from "./pages/Signup";
import Premium from "./pages/Premium";
import PitchSheet from "./pages/PitchSheet";
import Overview from "./pages/Overview";
import { RouteBoundary } from "./components/PageBoundary";
import BusinessProfile from "./pages/BusinessProfile";
import ShopBySlug from "./pages/ShopBySlug";
import DealByLink from "./pages/DealByLink";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import OurStory from "./pages/OurStory";
import ContactUs from "./pages/ContactUs";
import LyteMap from "./pages/LyteMap";
import SpotSheet from "./pages/SpotSheet";
import Following from "./pages/Following";
import LegalPage from "./pages/LegalPage";
import VerifyEmail from "./pages/VerifyEmail";
import FixPin from "./pages/FixPin";
import NotFound from "./pages/NotFound";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import Cities from "./pages/Cities";
import CityDeals from "./pages/CityDeals";
import JoinCity from "./pages/JoinCity";
import CityPitch from "./pages/CityPitch";
import { CookieConsent } from "./components/CookieConsent";
import { InstallPrompt } from "./components/InstallPrompt";
import { Toaster } from "./components/ui/toaster";
import { PageTracker } from "./components/PageTracker";
import HomeRoute from "./components/HomeRoute";
import MaintenanceGate from "./components/MaintenanceGate";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

// Sales material, admin eyes only. A signed-in shop owner is sent home rather than shown a page
// that then fails on the API — the sheets quote other shops' deals and our own counts.
const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.role === "admin" ? children : <Navigate to="/" replace />;
};

// Plans and prices are only relevant to a signed-in shop owner (and the admin who supports them).
const ShopOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.role === "business" || user.role === "admin" ? children : <Navigate to="/" replace />;
};

// Pricing is public by default; an admin can switch it off, which reverts to shop/admin-only.
const PricingRoute = () => {
  const cfg = useConfig();
  if (cfg == null) return null; // wait for the flag so we don't flash the wrong gate
  return cfg.pricingPublic === false ? <ShopOnly><Premium /></ShopOnly> : <Premium />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LanguageProvider>
        <LangSync />
        <LangGeoHint />
        <LocationProvider>
          <FxProvider>
            <FavouritesProvider>
            <SubscriptionsProvider>
            <div className="App">
              <BrowserRouter>
                <PageTracker />
                <MaintenanceGate>
                <RouteBoundary label="app">
                <Routes>
                  <Route element={<DashboardLayout />}>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/browse" element={<BrowseDeals />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/near-expiry" element={<NearExpiryPage />} />
                    <Route path="/businesses" element={<Businesses />} />
                    <Route path="/favourites" element={<Favourites />} />
                    <Route path="/my-deals" element={<MyDeals />} />
                    <Route path="/deal/:id" element={<DealDetail />} />
                    <Route path="/post-deal" element={<Protected><PostDeal /></Protected>} />
                    <Route path="/edit-deal/:id" element={<Protected><PostDeal /></Protected>} />
                    <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
                    <Route path="/analytics-features" element={<Navigate to="/" replace />} />
                    <Route path="/account" element={<Protected><Account /></Protected>} />
                    <Route path="/our-story" element={<OurStory />} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/lytemap" element={<LyteMap />} />
                    <Route path="/spot/:coord" element={<SpotSheet />} />
                    <Route path="/following" element={<Following />} />
                    <Route path="/premium" element={<ShopOnly><Premium /></ShopOnly>} />
                    <Route path="/pricing" element={<PricingRoute />} />
                    <Route path="/pitch" element={<PitchSheet />} />
                    <Route path="/overview" element={<Overview />} />
                    <Route path="/terms" element={<LegalPage kind="terms" />} />
                    <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/fix-pin" element={<FixPin />} />
                    <Route path="/refunds" element={<Navigate to="/terms" replace />} />
                    <Route path="/business/:name" element={<BusinessProfile />} />
                    <Route path="/s/:slug" element={<ShopBySlug />} />
                    <Route path="/s/:slug/:dealSlug" element={<DealByLink />} />
                    <Route path="/admin" element={<AdminOnly><AdminDashboard /></AdminOnly>} />
                    <Route path="/cities" element={<Cities />} />
                    <Route path="/join/:countrySlug/:citySlug" element={<JoinCity />} />
                    <Route path="/pitch/:countrySlug/:citySlug" element={<AdminOnly><CityPitch /></AdminOnly>} />
                    <Route path="/:countrySlug/:citySlug" element={<CityDeals />} />
                    <Route path="/:lang/:countrySlug/:citySlug" element={<CityDeals />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  <Route path="/login" element={<Login />} />
                  {/* Stays reachable during a full maintenance lockout so staff can get in. */}
                  <Route path="/staff-login" element={<Login portal />} />
                  <Route path="/staff-login/:slug" element={<Login portal />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                </Routes>
                </RouteBoundary>
                </MaintenanceGate>
                <CookieConsent />
                <InstallPrompt />
                <Toaster />
              </BrowserRouter>
            </div>
            </SubscriptionsProvider>
          </FavouritesProvider>
          </FxProvider>
        </LocationProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
